import { useId } from 'react';
import type { SearchScope } from '@/api/types';

export interface SearchScopeBarProps {
  scope: SearchScope;
  /** What "This folder" is called here: the folder's own name. */
  folderName: string;
  includeTrashJunk: boolean;
  /** The query carries `in:` terms, which choose the folders themselves. */
  overridden: boolean;
  onScopeChange: (scope: SearchScope) => void;
  onIncludeTrashJunkChange: (include: boolean) => void;
  /**
   * The merged view: there is no single "this folder", so the second choice is
   * each mailbox's Inbox — which is what that view lists.
   */
  unified?: boolean;
}

/**
 * Where a search looks: every folder, or the one the search started in.
 *
 * Two pressed-state chips rather than a select, because the choice is binary
 * and both halves should be readable at a glance; the Trash & Junk switch sits
 * beside them only while "All folders" is chosen, which is the only scope it
 * means anything in (Gmail leaves those two out of "all mail" too). A query
 * with `in:` terms names its own folders, so the control is shown but
 * disabled, with the reason, rather than silently doing nothing.
 */
export function SearchScopeBar({
  scope,
  folderName,
  includeTrashJunk,
  overridden,
  onScopeChange,
  onIncludeTrashJunkChange,
  unified = false,
}: SearchScopeBarProps) {
  const toggleId = useId();
  return (
    <div className="search-scope" role="group" aria-label="Search in">
      <span className="search-scope__label" aria-hidden="true">
        Search in
      </span>
      <button
        type="button"
        className="chip"
        aria-pressed={!overridden && scope === 'all'}
        disabled={overridden}
        onClick={() => onScopeChange('all')}
      >
        All folders
      </button>
      <button
        type="button"
        className="chip search-scope__folder"
        aria-pressed={!overridden && scope === 'folder'}
        disabled={overridden}
        onClick={() => onScopeChange('folder')}
        title={unified ? 'Each mailbox’s Inbox' : `Only ${folderName}`}
      >
        <span className="truncate">{unified ? 'Inboxes' : folderName}</span>
      </button>
      {overridden ? (
        <span className="search-scope__note muted">
          <code>in:</code> picks the folders
        </span>
      ) : (
        scope === 'all' && (
          <label className="search-scope__trash" htmlFor={toggleId}>
            <input
              id={toggleId}
              type="checkbox"
              checked={includeTrashJunk}
              onChange={(event) => onIncludeTrashJunkChange(event.target.checked)}
            />
            <span>Include Trash &amp; Junk</span>
          </label>
        )
      )}
    </div>
  );
}
